import { useCallback, useMemo, useState } from 'react';
import type {
  AlertConfigResponse,
  NotifyChannel,
  RecipientCreateBody,
  RecipientItem,
  RecipientPatchBody,
  Severity,
} from '../../api/alertConfigApi';

// 권위: docs/ARCH_DASHBOARD.md §4.9 섹션 4 — 수신자 CRUD.
// UI: 테이블 + [추가] 버튼 → 모달. 행 클릭 또는 [수정] → 수정 모달. [삭제] → 물리 삭제(DELETE).
// 감사 이력은 alert_config_history 에 before_value 로 보존된다.

const CHANNELS: NotifyChannel[] = ['email', 'teams', 'webpush'];
const SEVERITIES: Severity[] = ['FATAL', 'ERROR', 'WARN', 'INFO'];

interface Props {
  config: AlertConfigResponse;
  onCreate: (body: RecipientCreateBody) => void;
  onUpdate: (recipientId: number, body: RecipientPatchBody) => void;
  onDelete: (recipientId: number) => void;
  isBusy: boolean;
}

type ModalMode = { mode: 'add' } | { mode: 'edit'; item: RecipientItem } | null;

export function RecipientsSection({
  config,
  onCreate,
  onUpdate,
  onDelete,
  isBusy,
}: Props): JSX.Element {
  const [modal, setModal] = useState<ModalMode>(null);

  const recipients = config.recipients;

  const onToggleEnabled = useCallback(
    (item: RecipientItem): void => {
      onUpdate(item.recipientId, { enabled: !item.enabled });
    },
    [onUpdate]
  );

  return (
    <section className="as-card" data-testid="as-recipients-section">
      <div className="as-section__head">
        <div>
          <h3 className="as-section__title">5. 수신자</h3>
          <div className="as-section__sub">
            채널별 주소와 구독 severity 를 관리합니다. 삭제 시 DB 행이 즉시
            제거되며, 변경 내역은 감사 이력에 before_value 로 보존됩니다.
          </div>
        </div>
        <div className="as-section__actions">
          <button
            type="button"
            className="as-btn as-btn--primary"
            onClick={() => setModal({ mode: 'add' })}
            disabled={isBusy}
            data-testid="as-recipients-add"
          >
            추가
          </button>
        </div>
      </div>

      {recipients.length === 0 ? (
        <div className="as-info">등록된 수신자가 없습니다.</div>
      ) : (
        <table className="as-recipients">
          <thead>
            <tr>
              <th>channel</th>
              <th>address</th>
              <th>displayName</th>
              <th>severities</th>
              <th>enabled</th>
              <th className="as-recipients__action-col" />
            </tr>
          </thead>
          <tbody>
            {recipients.map((r) => (
              <tr
                key={r.recipientId}
                data-testid={`as-recipient-row-${r.recipientId}`}
              >
                <td>
                  <span className="as-badge">{r.channel}</span>
                </td>
                <td className="as-meta__value">{r.address}</td>
                <td>{r.displayName ?? '—'}</td>
                <td>
                  {r.severities.map((s) => (
                    <span key={s} className={`as-badge as-badge--${s}`}>
                      {s}
                    </span>
                  ))}
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={r.enabled}
                    disabled={isBusy}
                    onChange={() => onToggleEnabled(r)}
                    aria-label={`enable ${r.address}`}
                  />
                </td>
                <td className="as-recipients__action-col">
                  <div className="as-recipients__actions">
                    <button
                      type="button"
                      className="as-btn"
                      disabled={isBusy}
                      onClick={() => setModal({ mode: 'edit', item: r })}
                    >
                      수정
                    </button>
                    <button
                      type="button"
                      className="as-btn as-btn--danger"
                      disabled={isBusy}
                      onClick={() => {
                        if (window.confirm(
                          `수신자 "${r.displayName ?? r.address}" 를 삭제하시겠습니까?\n` +
                          `물리 삭제 — 되돌릴 수 없습니다.`,
                        )) {
                          onDelete(r.recipientId);
                        }
                      }}
                    >
                      삭제
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal && (
        <RecipientModal
          mode={modal.mode}
          initial={modal.mode === 'edit' ? modal.item : null}
          onClose={() => setModal(null)}
          onCreate={(body) => {
            onCreate(body);
            setModal(null);
          }}
          onUpdate={(id, body) => {
            onUpdate(id, body);
            setModal(null);
          }}
          isBusy={isBusy}
        />
      )}
    </section>
  );
}

// ── 모달 ────────────────────────────────────────────────

interface ModalProps {
  mode: 'add' | 'edit';
  initial: RecipientItem | null;
  onClose: () => void;
  onCreate: (body: RecipientCreateBody) => void;
  onUpdate: (recipientId: number, body: RecipientPatchBody) => void;
  isBusy: boolean;
}

function RecipientModal({
  mode,
  initial,
  onClose,
  onCreate,
  onUpdate,
  isBusy,
}: ModalProps): JSX.Element {
  const [channel, setChannel] = useState<NotifyChannel>(initial?.channel ?? 'email');
  const [address, setAddress] = useState<string>(initial?.address ?? '');
  const [displayName, setDisplayName] = useState<string>(
    initial?.displayName ?? ''
  );
  const [severities, setSeverities] = useState<Severity[]>(
    initial?.severities ?? ['FATAL', 'ERROR']
  );
  const [enabled, setEnabled] = useState<boolean>(initial?.enabled ?? true);

  const valid = useMemo(
    () => address.trim().length > 0 && severities.length > 0,
    [address, severities]
  );

  const toggleSev = (s: Severity): void => {
    setSeverities((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const submit = (): void => {
    if (!valid) return;
    if (mode === 'add') {
      const body: RecipientCreateBody = {
        channel,
        address: address.trim(),
        displayName: displayName.trim() ? displayName.trim() : null,
        severities,
        enabled,
      };
      onCreate(body);
    } else if (initial) {
      // 권위: dashboard-api PATCH /alert-config/recipients/{id} 는
      // RecipientUpdateRequest(channel/address/displayName/severities/enabled)
      // 전 필드를 수용한다 (병렬 작업으로 channel/address 변경 지원 추가됨).
      // PATCH semantics 상 변경된 필드를 모두 보낸다 — 서버는 (channel,address)
      // unique 제약 충돌 시 422 recipient_duplicate 로 회신.
      const body: RecipientPatchBody = {
        channel,
        address: address.trim(),
        displayName: displayName.trim() ? displayName.trim() : null,
        severities,
        enabled,
      };
      onUpdate(initial.recipientId, body);
    }
  };

  return (
    <div
      className="as-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-label="recipient"
    >
      <div className="as-modal">
        <div className="as-modal__header">
          <h3 className="as-modal__title">
            {mode === 'add' ? '수신자 추가' : '수신자 수정'}
          </h3>
          <button
            type="button"
            className="as-btn as-btn--ghost"
            onClick={onClose}
          >
            X
          </button>
        </div>
        <div className="as-modal__body">
          <div className="as-field">
            <label className="as-field__label">channel</label>
            <select
              className="as-select"
              value={channel}
              onChange={(e) => setChannel(e.target.value as NotifyChannel)}
              disabled={isBusy}
              data-testid="as-recipient-channel"
            >
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="as-field">
            <label className="as-field__label">address</label>
            <input
              className="as-input"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={
                channel === 'email'
                  ? 'ops@example.com'
                  : channel === 'teams'
                    ? 'webhook URL 또는 channel id'
                    : 'web-push endpoint'
              }
              disabled={isBusy}
              data-testid="as-recipient-address"
            />
          </div>

          <div className="as-field">
            <label className="as-field__label">display name (옵션)</label>
            <input
              className="as-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={isBusy}
            />
          </div>

          <div>
            <div className="as-field__label" style={{ marginBottom: 4 }}>
              severity 구독
            </div>
            <div className="as-chip-row">
              {SEVERITIES.map((s) => {
                const active = severities.includes(s);
                return (
                  <label
                    key={s}
                    className={`as-chip ${active ? 'as-chip--active' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      disabled={isBusy}
                      onChange={() => toggleSev(s)}
                    />
                    <span>{s}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <label className="as-chip">
            <input
              type="checkbox"
              checked={enabled}
              disabled={isBusy}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span>enabled</span>
          </label>
        </div>
        <div className="as-modal__actions">
          <button
            type="button"
            className="as-btn as-btn--ghost"
            onClick={onClose}
            disabled={isBusy}
          >
            취소
          </button>
          <button
            type="button"
            className="as-btn as-btn--primary"
            onClick={submit}
            disabled={!valid || isBusy}
            data-testid="as-recipient-submit"
          >
            저장
          </button>
        </div>
      </div>
    </div>
  );
}
